import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Trash2, FilePlus } from 'lucide-react';
import { api } from '../lib/api';

export default function CallFlow() {
  const qc = useQueryClient();
  const { data: scripts = [] } = useQuery({ queryKey: ['callflow'], queryFn: () => api.callflow.list() });
  const { data: samples = [] } = useQuery({ queryKey: ['callflow-samples'], queryFn: () => api.callflow.samples() });
  const [activeId, setActiveId] = useState<string | null>(null);
  const { data: active } = useQuery({
    queryKey: ['callflow', activeId],
    queryFn: () => activeId ? api.callflow.get(activeId) : null,
    enabled: !!activeId,
  });
  const [edited, setEdited] = useState<any>(null);

  const cur = edited ?? active;

  const save = useMutation({
    mutationFn: () => activeId
      ? api.callflow.update(activeId, cur)
      : api.callflow.create(cur).then((r: any) => { setActiveId(r.script_uuid); return r; }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['callflow'] }); setEdited(null); },
  });
  const remove = useMutation({
    mutationFn: () => api.callflow.remove(activeId!),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['callflow'] }); setActiveId(null); setEdited(null); },
  });

  function newFromSample(s: any) {
    setActiveId(null);
    setEdited({ name: s.name, lua_source: s.lua_source, description: s.name.replace(/_/g, ' '), enabled: true });
  }

  return (
    <div className="flex gap-4 h-full">
      <aside className="w-64 flex-shrink-0 space-y-3">
        <div className="card">
          <h2 className="font-semibold mb-2">Scripts</h2>
          <ul className="space-y-1 text-sm">
            {(scripts as any[]).map((s: any) => (
              <li key={s.script_uuid}>
                <button onClick={() => { setActiveId(s.script_uuid); setEdited(null); }}
                  className={`w-full text-left px-2 py-1 rounded ${activeId === s.script_uuid ? 'bg-primary text-white' : 'hover:bg-elevated'}`}>
                  {s.name}
                </button>
              </li>
            ))}
            {!(scripts as any[]).length && <li className="text-textSecondary">No scripts yet.</li>}
          </ul>
        </div>
        <div className="card">
          <h2 className="font-semibold mb-2">Templates</h2>
          <ul className="space-y-1 text-sm">
            {(samples as any[]).map((s: any) => (
              <li key={s.name}>
                <button onClick={() => newFromSample(s)} className="w-full text-left flex items-center gap-2 px-2 py-1 hover:bg-elevated rounded">
                  <FilePlus size={12} />{s.name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>
      <div className="flex-1 flex flex-col">
        {cur ? (
          <>
            <div className="flex items-center gap-3 mb-3">
              <input className="input flex-1" placeholder="script_name" value={cur.name || ''}
                onChange={e => setEdited({ ...cur, name: e.target.value })} />
              <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
              {activeId && <button onClick={() => remove.mutate()} className="btn-ghost text-danger inline-flex items-center gap-2"><Trash2 size={14} /> Delete</button>}
            </div>
            <textarea className="input font-mono text-xs h-[60vh]" value={cur.lua_source || ''}
              onChange={e => setEdited({ ...cur, lua_source: e.target.value })} />
          </>
        ) : (
          <div className="text-textSecondary">Select a script or pick a template to start.</div>
        )}
      </div>
    </div>
  );
}
